import { Box, Input, Typography } from '@mantine/core';
import { Link } from '@mantine/tiptap';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

/**
 * Maintenance notes and comments, written in TipTap and stored as HTML.
 *
 * The server sanitises on the way in, so what comes back is safe to render.
 */
export function RichTextField({
  value,
  onChange,
  label,
  description,
  error,
  minHeight = 160,
  placeholder,
}: {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  description?: string;
  error?: string;
  minHeight?: number;
  placeholder?: string;
}) {
  const editor = useEditor({
    extensions: [StarterKit, Link],
    content: value,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
  });

  // Reset when the record being edited changes underneath us, but never while typing.
  useEffect(() => {
    if (editor && !editor.isFocused && editor.getHTML() !== value) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  return (
    <Input.Wrapper label={label} description={description} error={error}>
      <RichTextEditor editor={editor} mt={label || description ? 4 : 0}>
        <RichTextEditor.Toolbar sticky stickyOffset={0}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Blockquote />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content mih={minHeight} data-placeholder={placeholder} />
      </RichTextEditor>
    </Input.Wrapper>
  );
}

/** Read-only rendering of stored HTML, used in maintenance logs and comment threads. */
export function RichTextView({ html }: { html?: string | null }) {
  if (!html) return null;
  return (
    <Typography p={0}>
      <Box fz="sm" dangerouslySetInnerHTML={{ __html: html }} />
    </Typography>
  );
}
